import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  getMyVenue, getAvailability, setAvailability, dateKey, todayKey,
  type VenueProfile, type AvailabilityDate,
} from '../../lib/venue';
import { ApiError } from '../../lib/apiClient';
import './VenuePages.css';

/**
 * Availability calendar.
 *
 * Three states per day, and the distinction matters:
 *
 *   unset    the venue has said nothing — neither offered nor withheld
 *   open     explicitly available
 *   blocked  explicitly unavailable
 *
 * Plus a fourth the venue does not control: HELD, written by the engine when a
 * booking is confirmed (`bookedByEvent`). Those cells are read-only here.
 *
 * That read-only treatment is a UI decision covering for an engine one:
 * setAvailability updates `available` and `notes` on conflict but leaves
 * `bookedByEvent` untouched, so the API would happily mark a confirmed date
 * available again and leave the calendar contradicting the booking. Rather than
 * offer an action that produces an inconsistent state, held dates are not
 * editable — to free one, decline or cancel the booking.
 *
 * Dates are keyed with dateKey(), never with `new Date()`: a Postgres DATE
 * serializes to midnight UTC, which is the previous calendar day for anyone west
 * of Greenwich.
 */

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function monthMatrix(year: number, month: number) {
  const first = new Date(year, month, 1);
  const days: (string | null)[] = Array.from({ length: first.getDay() }, () => null);
  const last = new Date(year, month + 1, 0).getDate();
  const pad = (n: number) => String(n).padStart(2, '0');
  for (let d = 1; d <= last; d += 1) {
    days.push(`${year}-${pad(month + 1)}-${pad(d)}`);
  }
  return days;
}

export function VenueCalendar() {
  const [venue, setVenue] = useState<VenueProfile | null>(null);
  const [noProfile, setNoProfile] = useState(false);
  const [dates, setDates] = useState<AvailabilityDate[]>([]);
  const [cursor, setCursor] = useState(() => {
    const n = new Date();
    return { year: n.getFullYear(), month: n.getMonth() };
  });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const load = useCallback(async (venueId: string) => {
    const { dates: rows } = await getAvailability(venueId);
    setDates(rows);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const v = await getMyVenue();
        if (cancelled) return;
        setVenue(v);
        if (!v) { setNoProfile(true); return; }
        await load(v.venueId);
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Could not load your calendar.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [load]);

  const byDate = useMemo(() => {
    const m = new Map<string, AvailabilityDate>();
    for (const d of dates) m.set(dateKey(d.date), d);
    return m;
  }, [dates]);

  const today = todayKey();
  const cells = useMemo(() => monthMatrix(cursor.year, cursor.month), [cursor]);

  function toggle(key: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function apply(available: boolean) {
    if (!venue || selected.size === 0) return;
    setBusy(true);
    setError(null);
    setFlash(null);
    try {
      await setAvailability(
        venue.venueId,
        [...selected].map((date) => ({ date, available, notes: notes.trim() || null })),
      );
      await load(venue.venueId);
      setFlash(`${selected.size} date${selected.size === 1 ? '' : 's'} marked ${available ? 'open' : 'blocked'}.`);
      setSelected(new Set());
      setNotes('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save those dates.');
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <p className="ven-status">Loading…</p>;

  if (noProfile) {
    return (
      <div className="ven-page ven-page--narrow">
        <p className="ven-status ven-muted">
          Create your venue profile before setting availability.
        </p>
        <div className="ven-actions">
          <Link className="button button--primary" to="/venue/profile">Create venue profile</Link>
        </div>
      </div>
    );
  }

  const monthLabel = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' })
    .format(new Date(cursor.year, cursor.month, 1));

  const step = (delta: number) => setCursor(({ year, month }) => {
    const d = new Date(year, month + delta, 1);
    return { year: d.getFullYear(), month: d.getMonth() };
  });

  return (
    <div className="ven-page">
      <header className="ven-header">
        <div>
          <h1 className="ven-title">Availability</h1>
          <p className="ven-lede">
            Select dates, then mark them open or blocked. Organizers can read this calendar
            when they are choosing a date.
          </p>
        </div>
      </header>

      {error && <p className="ven-error" role="alert">{error}</p>}
      {flash && <p className="ven-flash" role="status">{flash}</p>}

      <section className="ven-card">
        <div className="ven-cal-head">
          <button type="button" className="button button--ghost" onClick={() => step(-1)}>← Prev</button>
          <span className="ven-cal-month">{monthLabel}</span>
          <button type="button" className="button button--ghost" onClick={() => step(1)}>Next →</button>
        </div>

        <div className="ven-cal" role="grid" aria-label={`Availability for ${monthLabel}`}>
          {DOW.map((d) => <div key={d} className="ven-cal__dow">{d}</div>)}
          {cells.map((key, i) => {
            if (!key) return <div key={`blank-${i}`} className="ven-day ven-day--blank" />;
            const entry = byDate.get(key);
            const held = !!entry?.bookedByEvent;
            const past = key < today;
            const state = held ? 'booked'
              : entry ? (entry.available ? 'open' : 'blocked')
              : 'unset';
            const isSelected = selected.has(key);
            return (
              <button
                key={key}
                type="button"
                disabled={held || past || busy}
                aria-pressed={isSelected}
                title={held
                  ? 'Held by a confirmed booking, decline or cancel the booking to free it'
                  : entry?.notes ?? undefined}
                className={[
                  'ven-day',
                  state !== 'unset' ? `ven-day--${state}` : '',
                  past ? 'ven-day--past' : '',
                  isSelected ? 'ven-day--selected' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => toggle(key)}
              >
                <span className="ven-day__num">{Number(key.slice(8))}</span>
                {held && <span className="ven-day__tag">Booked</span>}
                {!held && state === 'open' && <span className="ven-day__tag">Open</span>}
                {!held && state === 'blocked' && <span className="ven-day__tag">Blocked</span>}
              </button>
            );
          })}
        </div>

        <div className="ven-legend">
          <span><span className="ven-legend__swatch ven-legend__swatch--open" />Open</span>
          <span><span className="ven-legend__swatch ven-legend__swatch--blocked" />Blocked</span>
          <span><span className="ven-legend__swatch ven-legend__swatch--booked" />Held by a confirmed booking</span>
          <span><span className="ven-legend__swatch" />Not set</span>
        </div>
      </section>

      <section className="ven-card">
        <h2 className="ven-section__title">
          {selected.size === 0
            ? 'Select dates to change them'
            : `${selected.size} date${selected.size === 1 ? '' : 's'} selected`}
        </h2>
        <p className="ven-section__note">
          Dates held by a confirmed booking cannot be changed here, decline or cancel the
          booking to free one. Past dates are read-only.
        </p>
        <label className="field">
          <span className="field__label">Note (optional, visible to organizers)</span>
          <input className="field__input" type="text" value={notes} disabled={busy}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Private hire / maintenance" />
        </label>
        <div className="ven-actions">
          <button type="button" className="button button--primary"
            disabled={busy || selected.size === 0} onClick={() => apply(true)}>
            Mark open
          </button>
          <button type="button" className="button button--secondary"
            disabled={busy || selected.size === 0} onClick={() => apply(false)}>
            Mark blocked
          </button>
          {selected.size > 0 && (
            <button type="button" className="button button--ghost" disabled={busy}
              onClick={() => setSelected(new Set())}>
              Clear selection
            </button>
          )}
        </div>
      </section>
    </div>
  );
}
